import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import {
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import SignaturePad from 'signature_pad';
import { EsAuthor } from '../../../../../models/EsAuthor';
import { AuthenticationService } from '../../../../../../entry-routes/authentication/authentication.service';
import {ApplicationConfigVarNames} from '../../../../../../entry-routes/configuration/application-config-var-names';
import { MatIconModule } from '@angular/material/icon';
import {UploadFileComponent, UploadFileConfig} from '../../../../tech-upload/component/upload-file/upload-file.component';
import { ConfigurationService } from '../../../../../../entry-routes/configuration/configuration.service';
import { PdfUtils } from '../../../../../utils/PdfUtils';
import { UserPreferenceLocalStorage } from '../../../../../services/user-preferences/userPreferenceLocalStorage';
import { TaskDetailsService } from '../../../../../redux/details/service/task/task-details.service';
import { SignatureOptions, SignatureDelimiters, SignaturePayload } from '../../../../../models/signature-payload';
import { UserSocketHttpResponse } from '../../../../../models/user-socket-http-response';
import { SignatureImageFile } from '../../../../../models/signature-image-file';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DeleteManualSignatureModalComponent } from '../../../../modal/delete-manual-signature-modal/delete-manual-signature-modal.component'
import { NexiaTooltipDirective } from '../../../../tooltip/nexia-tooltip.directive';
import { ModalResponse } from '../../../../../models/modal-response';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-manual-signature',
  standalone: true,
  imports: [ CommonModule, TranslateModule, MatRadioModule, MatFormFieldModule, MatInputModule, FormsModule, ReactiveFormsModule, MatIconModule, UploadFileComponent, NexiaTooltipDirective, MatDialogModule ],
  templateUrl: './manual-signature.component.html',
  styleUrls: ['./manual-signature.component.scss']
})
export class ManualSignatureComponent implements OnInit {

  @Input()
  taskInstanceId: string;

  @Input()
  subTaskFinished: boolean;

  @Input()
  token: string;

  @Output()
  isSigned: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  isError: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('signaturePadCanvas' , { static: true }) signaturePadCanvasRef: ElementRef;
  @ViewChild('radioWithFullNameSignatureDiv' , { static: true }) radioWithFullNameSignatureDivRef: ElementRef;
  @ViewChild('radioHandwrittenSignatureDiv' , { static: true }) radioHandwrittenSignatureDivRef: ElementRef;

  SIGNATURETYPE_RADIO_HANDWRITTEN = 'radioHandwrittenSignature';
  SIGNATURETYPE_RADIO_WITHFULLNAME = 'radioWithFullNameSignature';

  signatureType: string;
  fullNameForHandwrittenCtrl: FormControl<string>;
  fullNameAloneCtrl: FormControl<string>;
  saveHandwrittenSignatureCtrl: FormControl<any>;

  uploadFileConfig: UploadFileConfig;
  displayAutoSizeChange = false;


  signatureFormGroup: FormGroup< {
    fullNameForHandwritten: FormControl<string | null>;
    fullNameAlone: FormControl<string | null>;
    saveHandwrittenSignature: FormControl<boolean | null>;
  }>  ;

  private signaturePadCanvas: HTMLCanvasElement;
  private signaturePad: SignaturePad;
  private canvasWidth: number;
  private canvasHeight: number;
  private resizeValue = 0.75;
  private static MAX_WIDTH  = 390;
  private static MAX_HEIGHT  = 130;
  private static MIN_WIDTH  = 120;
  private static MIN_HEIGHT  = 40;
  public signed = false;
  public AUTO_RESIZE = true;
  public TO_SEND_WIDTH  = 210;
  public TO_SEND_HEIGHT  = 70;

  public TO_SEND_WIDTH_SMALL  = 150;
  public TO_SEND_WIDTH_DEFAULT  = this.TO_SEND_WIDTH;
  public TO_SEND_WIDTH_BIG  = 300;
  public invalidFormat =  false;

  constructor(private translateService: TranslateService,
              private fb: NonNullableFormBuilder,
              private authenticationService: AuthenticationService,
              private configurationService : ConfigurationService,
              private taskDetailsService : TaskDetailsService,
              private elementRef: ElementRef,
              protected dialog: MatDialog
              ) {

    const userConnectedLabel = EsAuthor.getUserLabel(this.authenticationService.getUserCredentials())

    this.fullNameForHandwrittenCtrl = this.fb.control(userConnectedLabel, Validators.required);
    this.fullNameAloneCtrl = this.fb.control(userConnectedLabel , Validators.required);
    this.saveHandwrittenSignatureCtrl= this.fb.control(false);

    this.signatureFormGroup = this.fb.group(
          { fullNameForHandwritten: this.fullNameForHandwrittenCtrl,
            fullNameAlone: this.fullNameAloneCtrl,
            saveHandwrittenSignature : this.saveHandwrittenSignatureCtrl
          });
    this.uploadFileConfig = new UploadFileConfig('SIGNATURE.ImageUpload', 1, +this.configurationService.getConfigVariable(ApplicationConfigVarNames.MAX_MB_DOC_FILE_SIZE) * 1e6, 'magic-regular:download');
  }

  ngOnInit(): void {
    this.signed = this.subTaskFinished;
    window.setTimeout(() => this.initSignaturePad());
  }

  private setSignaturePadCanvas( width: number, height: number, disableHeightCheck = false ) {
    if ( width <= ManualSignatureComponent.MAX_WIDTH && (disableHeightCheck || height <= ManualSignatureComponent.MAX_HEIGHT) &&
         width >= ManualSignatureComponent.MIN_WIDTH && (disableHeightCheck || height >= ManualSignatureComponent.MIN_HEIGHT)
        ) {
      this.canvasWidth = width;
      this.canvasHeight = height;

      this.signaturePadCanvas.width = this.canvasWidth;
      this.signaturePadCanvas.height = this.canvasHeight;

      this.signaturePadCanvas.style.width = this.canvasWidth+'px';
      this.signaturePadCanvas.style.height = this.canvasHeight+'px';

      this.signaturePad = new SignaturePad( this.signaturePadCanvas, {
        backgroundColor: 'rgb(255, 255, 255,0)'
      } );
    }
  }


  private initSignaturePad() {
    if ( !!this.signaturePadCanvasRef ) {
      const availableWidth = Math.floor(this.elementRef.nativeElement.clientWidth) - 20;
      ManualSignatureComponent.MAX_WIDTH = availableWidth > 0 ? availableWidth : 390;
      ManualSignatureComponent.MAX_HEIGHT = Math.floor(ManualSignatureComponent.MAX_WIDTH /3.4);

      this.signaturePadCanvas = this.signaturePadCanvasRef.nativeElement as HTMLCanvasElement

      const localStorageSignatureImageFile : SignatureImageFile = this.getLocalStorageSignatureImageFile();
      if (localStorageSignatureImageFile) {
        const size = this.scaleToFit(localStorageSignatureImageFile.width, localStorageSignatureImageFile.height);
        this.resizeCanvasAndPadWithImage(size.width, size.height, localStorageSignatureImageFile.signatureImageFileBase64, true);
      } else {
        this.setSignaturePadCanvas(ManualSignatureComponent.MAX_WIDTH, ManualSignatureComponent.MAX_HEIGHT);
      }

    }
  }


  onSignatureFormSubmit() {
    let signaturePayload: SignaturePayload ;

    let waitResizedDelayBeforeSend = 0;
    if ( this.signatureType === this.SIGNATURETYPE_RADIO_HANDWRITTEN && this.AUTO_RESIZE ) {
      const signatureBase64DataUrl = this.signaturePad.toDataURL('image/png');

      if ( !!this.saveHandwrittenSignatureCtrl.value ) {
        const signatureImageFile = new SignatureImageFile( signatureBase64DataUrl, this.canvasWidth, this.canvasHeight )
        this.setLocalStorageSignatureImageFile( signatureImageFile );
      }

      this.resizeCanvasAndPadWithImage(this.TO_SEND_WIDTH, this.TO_SEND_HEIGHT, signatureBase64DataUrl,true);
      waitResizedDelayBeforeSend = 10; // 10ms : délai nécessaire pour que le resize de l'image ai le temps de se faire
    }

    window.setTimeout( () => {
      const signature_options = new SignatureOptions( new SignatureDelimiters( '[[', ']]' ) );
      signaturePayload = new SignaturePayload( signature_options );

      if ( this.signatureType === this.SIGNATURETYPE_RADIO_HANDWRITTEN ) {
        const signatureBase64DataUrl = this.signaturePad.toDataURL( 'image/png' );

        signaturePayload.datas.fullName = this.fullNameForHandwrittenCtrl.value;
        signaturePayload.datas.signature = signatureBase64DataUrl;

      } else if ( this.signatureType === this.SIGNATURETYPE_RADIO_WITHFULLNAME ) {
        signaturePayload.datas.fullName = this.fullNameAloneCtrl.value;
      }

      if ( !!signaturePayload ) {

        this.taskDetailsService.generateWithKeyValues( this.taskInstanceId, signaturePayload, this.token ).subscribe(
            {
              next: ( response: UserSocketHttpResponse ) => {
                console.debug( 'generateWithKeyValues result : ', response );
                if ( this.AUTO_RESIZE ) {
                  this.initSignaturePad(); // redimentionnement d'origine
                }
                this.signed = true;
                this.isSigned.emit()
              },
              error: ( httpErrorResponse: HttpErrorResponse ) => {
                let errorMessage : string  = this.translateService.instant('SIGNATURE.unknownError' );
                if(httpErrorResponse.status === 403) {
                    errorMessage = this.translateService.instant('SIGNATURE.forbiddenError' );
                }
                console.error( errorMessage +' :', httpErrorResponse );
                this.isError.emit(errorMessage)
              }
            }
        );

      }// fin code du settimeout
    } , waitResizedDelayBeforeSend ); // fin settimeout
  }

  public signatureClear(): void {
    this.signaturePad.clear();
    this.setSignaturePadCanvas(ManualSignatureComponent.MAX_WIDTH, ManualSignatureComponent.MAX_HEIGHT);
    this.TO_SEND_HEIGHT = this.TO_SEND_WIDTH /3;

    const localStorageSignatureImageFile : SignatureImageFile = this.getLocalStorageSignatureImageFile();
    if (localStorageSignatureImageFile) {
      const dialogRefResult: MatDialogRef<any> = this.dialog.open( DeleteManualSignatureModalComponent, {
        width: '650px',
        disableClose: false,
        /* data: { gifLoader: loadGif} */
        data: {}
      } );

      dialogRefResult.afterClosed().subscribe( ( response: ModalResponse ) => {
        switch ( response ) {
          case ModalResponse.CLICK_NO: {
            console.log( response );
            break;
          }
          case ModalResponse.CLICK_YES: {
            console.log( response );
            this.setLocalStorageSignatureImageFile( null );
            break;
          }
        }
      } );
    }
  }

  public signaturePadIsEmpty(): boolean {
    return !this.signaturePad || this.signaturePad.isEmpty();
  }

  // cache icone agrandir
  public signaturePadIsEmptyOrMaxSize(): boolean {
    return this.AUTO_RESIZE || (this.signaturePad.isEmpty() || this.signaturePadCanvas.width >= (ManualSignatureComponent.MAX_WIDTH -15));
  }

  // cache icone réduire
  public signaturePadIsEmptyOrMinSize(): boolean {
    return this.AUTO_RESIZE || ( this.signaturePad.isEmpty() || this.signaturePadCanvas.width <= (ManualSignatureComponent.MIN_WIDTH+ 35 ));
  }


  handleFileUpload(fileToUpload: File){
    this.invalidFormat = false;
    if(fileToUpload.type.toLowerCase().indexOf("image")<0) {
      this.invalidFormat = true;
    }

    const fileReader = new FileReader();

    fileReader.onload = () => {
      const tmpImg = new Image();
      tmpImg.onload =  ()=>  {
        const ratio = tmpImg.width/tmpImg.height;
        const size = this.scaleToFit(this.canvasWidth, this.canvasWidth / ratio);
        this.resizeCanvasAndPadWithImage(size.width, size.height, fileReader.result.toString(), true );
      }
      tmpImg.src = fileReader.result.toString();
    };

    fileReader.readAsDataURL(fileToUpload);
  }

  public downloadSignatureImage(): void {
    if (!this.signaturePadIsEmpty()) {
      PdfUtils.fileDownload(PdfUtils.dataURLToBlob(this.signaturePad.toDataURL('image/png')), 'signature.png');
    }
  }


  signatureTypeChoiceChange( matRadioChange: MatRadioChange ) {
    if (!!matRadioChange.value) {
      this.signatureType = matRadioChange.value;
      // on joue sur la propriété de style display plutôt que d'utiliser le ngIf
      // car le Dom du canva doit exister (dans le conteneur div radioHandwrittenSignature)
      // ... pour être initialisé correctement
      if (this.signatureType === this.SIGNATURETYPE_RADIO_WITHFULLNAME) {
        this.radioWithFullNameSignatureDivRef.nativeElement.style.display = 'block';
        this.radioHandwrittenSignatureDivRef.nativeElement.style.display = 'none';
      } else {
        if (this.signatureType === this.SIGNATURETYPE_RADIO_HANDWRITTEN) {
          this.radioWithFullNameSignatureDivRef.nativeElement.style.display = 'none';
          this.radioHandwrittenSignatureDivRef.nativeElement.style.display = 'block';
        }
      }
    }
  }


  public setLocalStorageSignatureImageFile(signatureImageFile: SignatureImageFile): void {
    const userConfigString: string = localStorage.getItem('' + this.authenticationService.getUserCredentials().id);
    let userConfig : UserPreferenceLocalStorage  = JSON.parse(userConfigString)
    if(userConfig == null){
      userConfig = new UserPreferenceLocalStorage();
    }
    userConfig.signatureImageFile = signatureImageFile;
    localStorage.setItem('' + this.authenticationService.getUserCredentials().id, JSON.stringify(userConfig));
  }


  public getLocalStorageSignatureImageFile(): SignatureImageFile {
    let signatureImageFile : SignatureImageFile;
    const storageDataByUserId: string = localStorage.getItem('' + this.authenticationService.getUserCredentials().id);
    if (storageDataByUserId) {
      const userConfig : UserPreferenceLocalStorage  = JSON.parse(storageDataByUserId)
      signatureImageFile = userConfig.signatureImageFile;

    }
    return signatureImageFile;
  }

  private scaleToFit(width: number, height: number): { width: number; height: number } {
    const scale = Math.min(ManualSignatureComponent.MAX_WIDTH / width, ManualSignatureComponent.MAX_HEIGHT / height, 1);
    const targetWidth = Math.min(Math.max(Math.round(width * scale), ManualSignatureComponent.MIN_WIDTH), ManualSignatureComponent.MAX_WIDTH);
    return { width: targetWidth, height: Math.round(targetWidth * height / width) };
  }

  private resizeCanvasAndPadWithImage(width, height, signatureBase64DataUrl, disableHeightCheck = false) {
    const ratio = width/height;
    this.TO_SEND_HEIGHT = this.TO_SEND_WIDTH /ratio;
    this.setSignaturePadCanvas(width, height, disableHeightCheck );
    if (!this.signaturePad) {
      return;
    }

    const options = {
      width: this.canvasWidth,
      height: this.canvasHeight
    }
    this.signaturePad.fromDataURL(signatureBase64DataUrl, options);
  }

  resizeSignatureCompress() {
    const signatureBase64DataUrl = this.signaturePad.toDataURL('image/png');
    this.resizeCanvasAndPadWithImage(this.canvasWidth * this.resizeValue, this.canvasHeight * this.resizeValue, signatureBase64DataUrl);
  }

  resizeSignatureExpand() {
    if (this.canvasWidth<ManualSignatureComponent.MAX_WIDTH) {
      const signatureBase64DataUrl = this.signaturePad.toDataURL('image/png');
      this.resizeCanvasAndPadWithImage(this.canvasWidth / this.resizeValue, this.canvasHeight / this.resizeValue, signatureBase64DataUrl);
    }
  }

  displayAutoSizeChangeCombo() {
    this.displayAutoSizeChange = true;
  }

  autoSizeChange( $event: Event ) {
    console.debug("autoSizeChange event : ", $event);
    const selectElement = $event.target as HTMLSelectElement;
    this.TO_SEND_WIDTH = parseInt(selectElement.value);
    this.TO_SEND_HEIGHT = this.TO_SEND_WIDTH /3;
  }
}
